import React, { useEffect, useRef, useState } from "react";
import PageHeaderContent from "../../components/pageHeaderContent";
import "./styles.scss";

/**
 * Portfolio page:
 * - Attempts to show LinkedIn profile in an iframe.
 * - Falls back to a "Visit LinkedIn" button if the iframe is blocked.
 * - Provides a single Download CV button (downloads /Shubham_Thakare_Resume.pdf).
 *
 * Note: Many sites (including LinkedIn) send headers that prevent framing (X-Frame-Options),
 * so embedding may fail in modern browsers / on many hosts. This component detects that
 * and shows a fallback button that opens the LinkedIn profile in a new tab.
 */

const LINKEDIN_URL = "https://www.linkedin.com/in/shubhamthakarecode/";
const RESUME_PATH = "/Shubham_Thakare_Resume.pdf";

const projects = [
  {
    title: "Cloud Order Management",
    description:
      "Full stack order management app using React + Spring Boot and Oracle DB. Implemented REST APIs, JWT auth, and performance optimizations.",
    image: require("../../images/image1.jpg").default,
    link: "https://github.com/bestshubhamever/order-management",
  },
  {
    title: "Payroll Management System",
    description:
      "Payroll & HR system with microservices backend, reporting and secure authentication.",
    image: require("../../images/image2.jpg").default,
    link: "https://github.com/bestshubhamever/payprops",
  },
  {
    title: "Frontend UI Kit & Components",
    description:
      "Reusable React component library and design system used across projects to ensure consistent UI patterns.",
    image: require("../../images/image3.jpg").default,
    link: "https://github.com/bestshubhamever/ui-kit",
  },
];

const Portfolio = () => {
  const iframeRef = useRef(null);
  const [iframeBlocked, setIframeBlocked] = useState(false);

  // Detect iframe blocking: try to access iframe document after load timeout.
  useEffect(() => {
    const timer = setTimeout(() => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      try {
        // If the iframe is accessible, contentDocument will not throw and we can check its location.
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        // If LinkedIn blocks framing, accessing doc may throw or be cross-origin; treat as blocked.
        // Some browsers won't throw but will set a blank doc — do a simple check:
        if (!doc || doc.location.href === "about:blank") {
          setIframeBlocked(true);
        } else {
          setIframeBlocked(false);
        }
      } catch (e) {
        setIframeBlocked(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="portfolio" className="portfolio">
      <PageHeaderContent headerText="Portfolio" icon={<></>} />

      <div className="portfolio__embed">
        <h3 className="portfolio__embed__title">LinkedIn Preview</h3>

        <div className="portfolio__embed__frame-wrap" aria-live="polite">
          {/* Try to embed LinkedIn. Many hosts (LinkedIn) will block framing; fallback logic handles it. */}
          <iframe
            ref={iframeRef}
            title="LinkedIn profile"
            src={LINKEDIN_URL}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            className="portfolio__embed__iframe"
          />
          {iframeBlocked && (
            <div className="portfolio__embed__fallback">
              <p>
                LinkedIn cannot be embedded in this browser or host due to security
                restrictions. Click the button below to open the profile in a new tab.
              </p>
              <div className="portfolio__embed__actions">
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn visit-linkedin"
                >
                  Open LinkedIn Profile
                </a>

                <a
                  href={RESUME_PATH}
                  download
                  className="btn download-cv"
                >
                  Download CV
                </a>
              </div>
            </div>
          )}

          {/* If iframe is not blocked, still show a Download CV button beneath it for convenience */}
          {!iframeBlocked && (
            <div className="portfolio__embed__actions-inline">
              <a href={RESUME_PATH} download className="btn download-cv">
                Download CV
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn visit-linkedin subtle"
                style={{ marginLeft: 12 }}
              >
                Open in new tab
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="portfolio__content">
        <h3 className="portfolio__projects__title">Projects</h3>
        <div className="portfolio__projects">
          {projects.map((p, i) => (
            <div className="project-card" key={i}>
              <div className="project-card__image">
                <img src={p.image} alt={p.title} />
              </div>
              <div className="project-card__info">
                <h4>{p.title}</h4>
                <p>{p.description}</p>
                <a href={p.link} target="_blank" rel="noopener noreferrer" className="btn view-btn">
                  View on GitHub
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
